import React from 'react';
import PropTypes from 'prop-types';
import { CloseBtn } from '@meronex/components';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import LinearProgress from '@material-ui/core/LinearProgress';
import Checkbox from '@material-ui/core/Checkbox';
import Tooltip from '@material-ui/core/Tooltip';
import { useTheme } from '@material-ui/core/styles';
import { toast } from 'react-toastify';
import { App } from '@meronex/app';

import IHealth from '@meronex/icons/fi/FiShield';
import ICheck from '@meronex/icons/md/MdCheckCircle';
import IWarn from '@meronex/icons/md/MdWarning';
import IError from '@meronex/icons/md/MdError';
import IFix from '@meronex/icons/fi/FiBriefcase';
import IExport from '@meronex/icons/fi/FiDownload';

import { storesMng } from '../../api/storesMng';
import { exportToExcel } from '../../../../utils/exportToExcel';
import { createProgressTracker } from '../../../../utils/progressTracker';

const { server } = App;

// Tab definitions with severity ordering
const TABS = [
  {
    key: 'mismatches', label: 'Mismatches', severity: 'error',
    description: 'Qty drift — the stored quantity doesn\'t match what transaction history says it should be. A transaction may have been lost or duplicated. Use Auto-Correct to fix.\n\nRate drift — each In transaction computes a weighted average cost and saves it to the item. If the item\'s rate doesn\'t match, someone used Edit Rate on the item detail page to override it. This may be intentional — review whether the current rate is correct.',
  },
  {
    key: 'negative', label: 'Negative Qty', severity: 'error',
    description: 'Items showing a negative quantity on hand. This usually means more was issued (Out) than was ever received (In) — often caused by back-to-back transactions processed out of order, or a missing receiving entry. Use Correct All to Zero to reset these items.',
  },
  {
    key: 'outOfStock', label: 'Out of Stock', severity: 'warning',
    description: 'Items with zero quantity on hand — fully depleted. These items had stock at some point but have since been fully consumed or transferred out. Review whether they need restocking, or if they can be archived to reduce clutter.',
  },
  {
    key: 'reorder', label: 'Reorder', severity: 'warning',
    description: 'Items with active consumption that need attention. Either they have no reorder level set (so you won\'t get low-stock alerts), or they\'re already below their reorder level and need restocking.',
  },
  {
    key: 'archive', label: 'Archive', severity: 'info',
    description: 'Items with zero stock and no consumption in the last 90 days. These are taking up space in your store without being used. Consider archiving them to keep the store clean.',
  },
  {
    key: 'stale', label: 'Stale Items', severity: 'info',
    description: 'Items that exist in the store but have zero transaction history — never received, issued, transferred, or modified. These were likely added to the store but never actually used. Consider removing them to keep the store clean.',
  },
  {
    key: 'modifies', label: 'Frequent Modifies', severity: 'warning',
    description: 'Items that have been manually adjusted (via Modify) more than 3 times. A Modify sets the quantity directly rather than going through In/Out. Frequent modifications may indicate a process gap — for example, stock being received or consumed without proper transactions.',
  },
  {
    key: 'rates', label: 'Missing Rates', severity: 'warning',
    description: 'Items with no unit rate (cost per unit) on record. This happens when an item has never been received with a price, so its inventory value cannot be calculated. To fix, do an In transaction with a unit rate, or use Modify to set the rate directly.',
  },
];

function getTabCount(data, tabKey) {
  if (!data) return 0;
  switch (tabKey) {
    case 'mismatches':
      return data.mismatchedItems || (data.mismatches ? data.mismatches.length : 0);
    case 'negative':
      return data.negativeQuantityCount || (data.negativeQuantities ? data.negativeQuantities.length : 0);
    case 'outOfStock':
      return data.outOfStockCount || (data.outOfStock ? data.outOfStock.length : 0);
    case 'reorder':
      return data.stockRecommendations
        ? data.stockRecommendations.filter((r) => r.recommendation !== 'archive').length
        : 0;
    case 'archive':
      return data.stockRecommendations
        ? data.stockRecommendations.filter((r) => r.recommendation === 'archive').length
        : 0;
    case 'stale':
      return data.staleItemCount || (data.staleItems ? data.staleItems.length : 0);
    case 'modifies':
      return data.frequentModifyCount || (data.frequentModifies ? data.frequentModifies.length : 0);
    case 'rates':
      return data.missingRateCount || (data.warnings ? data.warnings.length : 0);
    default:
      return 0;
  }
}

function getFirstTabWithIssues(data) {
  // Return first tab with count > 0, in severity order (TABS is already ordered)
  for (const tab of TABS) {
    if (getTabCount(data, tab.key) > 0) return tab.key;
  }
  return 'mismatches';
}

function HealthCheckDialog(props) {
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  const { open, onClose, store } = props;

  const [state, setState] = React.useState('idle'); // idle, running, done, error
  const [result, setResult] = React.useState(null);
  const [lastResult, setLastResult] = React.useState(null);
  const [progress, setProgress] = React.useState(0);
  const [progressMessage, setProgressMessage] = React.useState('');
  const [correcting, setCorrecting] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState('mismatches');
  const [includeInactive, setIncludeInactive] = React.useState(false);
  const [applyingReorder, setApplyingReorder] = React.useState(false);
  const [applyProgress, setApplyProgress] = React.useState({ done: 0, total: 0 });
  const [appliedItems, setAppliedItems] = React.useState(new Map());
  const [archivedItems, setArchivedItems] = React.useState(new Set());
  const [archiving, setArchiving] = React.useState(false);
  const [archiveProgress, setArchiveProgress] = React.useState({ done: 0, total: 0 });
  const [stale, setStale] = React.useState(false);

  const cancelledRef = React.useRef(false);
  const trackerRef = React.useRef(null);

  const cleanupProgress = () => {
    if (trackerRef.current) {
      trackerRef.current.cleanup();
      trackerRef.current = null;
    }
  };

  // Cleanup progress subscription on unmount
  React.useEffect(() => {
    return () => { cleanupProgress(); };
  }, [open, store?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const runVerification = async () => {
    cancelledRef.current = false;
    setState('running');
    setProgress(0);
    setProgressMessage('Starting verification...');
    setStale(false);
    setResult(null);
    setAppliedItems(new Map());
    setArchivedItems(new Set());

    const progressId = Math.random().toString(36).substring(2, 15);

    try {
      const tracker = createProgressTracker(server);
      trackerRef.current = tracker;

      let done = false;
      let completionTimer = null;

      const finalize = (res) => {
        if (done || cancelledRef.current) return;
        done = true;
        if (completionTimer) clearTimeout(completionTimer);
        cleanupProgress();
        setResult(res);
        setLastResult(res);
        setActiveTab(getFirstTabWithIssues(res));
        setState('done');
        toast.success(`Verification complete — ${res.mismatchedItems} mismatches found`);
      };

      // Subscribe and set up reactive progress tracking (with visibility safety net)
      await tracker.start(progressId, {
        onUpdate: (doc) => {
          if (cancelledRef.current || done) return;

          const p = typeof doc.progress === 'number' ? doc.progress : 0;
          setProgress(p);
          if (p >= 100) {
            setProgressMessage('Finalizing report...');
          } else {
            setProgressMessage(doc.currentItem || `${doc.processed || 0} of ${doc.total || 0} items...`);
          }

          // Safety net: if progress hits 100% but method call is stuck (e.g. tab was backgrounded),
          // wait a few seconds then fetch the result directly
          if (p >= 100 && !completionTimer) {
            completionTimer = setTimeout(async () => {
              if (done || cancelledRef.current) return;
              console.warn('[HealthCheck] Method stuck at 100% — fetching result directly');
              try {
                const fallback = await storesMng.getLastVerificationResult(store._id);
                if (fallback) finalize(fallback);
              } catch (e) {
                console.error('[HealthCheck] Fallback fetch failed:', e);
              }
            }, 5000);
          }
        },
        onCompleted: (doc) => {
          // Progress marked completed — method result will finalize via await below
        },
        onError: (doc) => {
          if (cancelledRef.current || done) return;
          done = true;
          if (completionTimer) clearTimeout(completionTimer);
          setState('error');
          setProgressMessage(doc.error || 'Verification failed');
          cleanupProgress();
        },
        onStale: () => {
          if (!cancelledRef.current && !done) setStale(true);
        },
        staleTimeout: 60000,
      });

      // Run the verification
      const res = await storesMng.verifyStoreIntegrity({
        storeId: store._id,
        progressId,
        includeInactive,
      });

      finalize(res);
    } catch (err) {
      cleanupProgress();
      console.error('Verification failed:', err);
      setState('error');
      setProgressMessage(err.message || 'Verification failed');
      toast.error('Verification failed');
    }
  };

  const correctAll = async () => {
    if (!result || !result.mismatches || result.mismatches.length === 0) return;

    setCorrecting(true);
    let corrected = 0;
    let failed = 0;

    for (const mismatch of result.mismatches) {
      if (!mismatch.quantityMatch) {
        try {
          await storesMng.correctStoreItemDrift({
            storeItemId: mismatch.storeItemId,
            derivedQuantity: mismatch.derivedQuantity,
          });
          corrected++;
        } catch (e) {
          console.error(`Failed to correct ${mismatch.itemName}:`, e);
          failed++;
        }
      }
    }

    setCorrecting(false);

    if (corrected > 0) {
      toast.success(`Corrected ${corrected} item(s)`);
      runVerification();
    }
    if (failed > 0) {
      toast.error(`Failed to correct ${failed} item(s)`);
    }
  };

  const correctNegatives = async () => {
    if (!result || !result.negativeQuantities || result.negativeQuantities.length === 0) return;

    setCorrecting(true);
    let corrected = 0;
    let failed = 0;

    for (const item of result.negativeQuantities) {
      try {
        await storesMng.correctStoreItemDrift({
          storeItemId: item.storeItemId,
          derivedQuantity: 0,
        });
        corrected++;
      } catch (e) {
        console.error(`Failed to correct ${item.itemName}:`, e);
        failed++;
      }
    }

    setCorrecting(false);

    if (corrected > 0) {
      toast.success(`Corrected ${corrected} negative item(s) to zero`);
      runVerification();
    }
    if (failed > 0) {
      toast.error(`Failed to correct ${failed} item(s)`);
    }
  };

  const getDateStr = (data) => {
    if (!data || !data.verifiedAt) return '';
    return new Date(data.verifiedAt).toISOString().split('T')[0];
  };

  const exportMismatches = () => {
    const data = result || lastResult;
    if (!data || !data.mismatches || data.mismatches.length === 0) return;

    exportToExcel(
      data.mismatches.map((item) => ({
        'Item Name': item.itemName,
        'Item Code': item.itemCode,
        'Unit': item.itemUnit,
        'Stored Quantity': item.storedQuantity,
        'Derived Quantity': item.derivedQuantity,
        'Quantity Drift': item.quantityDrift,
        'Issue': !item.quantityMatch
          ? 'Quantity mismatch'
          : item.rateSanityIssue || item.lastTxRateIssue || 'Rate issue',
      })),
      `health-check-${store.name}-${getDateStr(data)}.xlsx`
    );
    toast.success('Mismatches exported');
  };

  const exportNegatives = () => {
    const data = result || lastResult;
    if (!data || !data.negativeQuantities || data.negativeQuantities.length === 0) return;

    exportToExcel(
      data.negativeQuantities.map((item) => ({
        'Item Name': item.itemName,
        'Item Code': item.itemCode,
        'Stored Quantity': item.quantity,
        'Derived Quantity': item.derivedQuantity != null ? item.derivedQuantity : '',
        'Drift': item.derivedQuantity != null ? parseFloat((item.quantity - item.derivedQuantity).toFixed(4)) : '',
      })),
      `negative-quantities-${store.name}-${getDateStr(data)}.xlsx`
    );
    toast.success('Negative quantities exported');
  };

  const exportStale = () => {
    const data = result || lastResult;
    if (!data || !data.staleItems || data.staleItems.length === 0) return;

    exportToExcel(
      data.staleItems.map((item) => ({
        'Item Name': item.itemName,
        'Item Code': item.itemCode,
        'Stored Quantity': item.storedQuantity,
      })),
      `stale-items-${store.name}-${getDateStr(data)}.xlsx`
    );
    toast.success('Stale items exported');
  };

  const exportModifies = () => {
    const data = result || lastResult;
    if (!data || !data.frequentModifies || data.frequentModifies.length === 0) return;

    exportToExcel(
      data.frequentModifies.map((item) => ({
        'Item Name': item.itemName,
        'Item Code': item.itemCode,
        'Modify Count': item.modifyCount,
      })),
      `frequent-modifies-${store.name}-${getDateStr(data)}.xlsx`
    );
    toast.success('Frequent modifies exported');
  };

  const exportOutOfStock = () => {
    const data = result || lastResult;
    if (!data || !data.outOfStock || data.outOfStock.length === 0) return;

    exportToExcel(
      data.outOfStock.map((item) => ({
        'Item Name': item.itemName,
        'Item Code': item.itemCode,
        'Unit': item.itemUnit || '',
        'Avg Monthly': item.avgMonthlyConsumption != null ? item.avgMonthlyConsumption : '',
        'Days Inactive': item.daysSinceLastOut != null ? item.daysSinceLastOut : '',
        'Recommendation': item.recommendation || '',
      })),
      `out-of-stock-${store.name}-${getDateStr(data)}.xlsx`
    );
    toast.success('Out of stock items exported');
  };

  const exportMissingRates = () => {
    const data = result || lastResult;
    if (!data || !data.warnings || data.warnings.length === 0) return;

    exportToExcel(
      data.warnings.map((item) => ({
        'Item Name': item.itemName,
        'Item Code': item.itemCode,
        'Unit': item.itemUnit || '',
        'Stored Quantity': item.storedQuantity,
      })),
      `missing-rates-${store.name}-${getDateStr(data)}.xlsx`
    );
    toast.success('Missing rates exported');
  };

  const applyReorderSuggestion = async (item) => {
    try {
      await storesMng.updateStoreItemThreshold({
        storeItemId: item.storeItemId,
        lowStockThreshold: item.suggestedReorderLevel,
      });
      setAppliedItems((prev) => new Map([...prev, [item.storeItemId, item.suggestedReorderLevel]]));
      toast.success(`Set reorder level for ${item.itemName} to ${item.suggestedReorderLevel}`);
    } catch (e) {
      console.error(`Failed to apply reorder for ${item.itemName}:`, e);
      toast.error(`Failed: ${item.itemName}`);
    }
  };

  const applyAllReorderSuggestions = async () => {
    const data = result || lastResult;
    if (!data || !data.stockRecommendations) return;

    const applicable = data.stockRecommendations.filter(
      (r) => (r.recommendation === 'set_reorder' || r.recommendation === 'reorder_now' || r.recommendation === 'review')
        && r.suggestedReorderLevel > 0
        && !appliedItems.has(r.storeItemId)
    );

    if (applicable.length === 0) return;

    setApplyingReorder(true);
    setApplyProgress({ done: 0, total: applicable.length });

    let done = 0;
    let failed = 0;

    for (const item of applicable) {
      try {
        await storesMng.updateStoreItemThreshold({
          storeItemId: item.storeItemId,
          lowStockThreshold: item.suggestedReorderLevel,
        });
        done++;
        setApplyProgress({ done, total: applicable.length });
        setAppliedItems((prev) => new Map([...prev, [item.storeItemId, item.suggestedReorderLevel]]));
      } catch (e) {
        console.error(`Failed to apply reorder for ${item.itemName}:`, e);
        failed++;
        done++;
        setApplyProgress({ done, total: applicable.length });
      }
    }

    setApplyingReorder(false);
    toast.success(`Applied reorder levels to ${done - failed} item(s)${failed > 0 ? `, ${failed} failed` : ''}`);
  };

  const archiveItem = async (item) => {
    try {
      await storesMng.archiveStoreItem(item.storeItemId);
      setArchivedItems((prev) => new Set([...prev, item.storeItemId]));
      toast.success(`Archived ${item.itemName}`);
    } catch (e) {
      console.error(`Failed to archive ${item.itemName}:`, e);
      toast.error(`Failed to archive ${item.itemName}`);
    }
  };

  const archiveAllItems = async (items) => {
    const toArchive = items.filter((r) => !archivedItems.has(r.storeItemId));
    if (toArchive.length === 0) return;

    setArchiving(true);
    setArchiveProgress({ done: 0, total: toArchive.length });

    let done = 0;
    let failed = 0;

    for (const item of toArchive) {
      try {
        await storesMng.archiveStoreItem(item.storeItemId);
        done++;
        setArchiveProgress({ done, total: toArchive.length });
        setArchivedItems((prev) => new Set([...prev, item.storeItemId]));
      } catch (e) {
        console.error(`Failed to archive ${item.itemName}:`, e);
        failed++;
        done++;
        setArchiveProgress({ done, total: toArchive.length });
      }
    }

    setArchiving(false);
    toast.success(`Archived ${done - failed} item(s)${failed > 0 ? `, ${failed} failed` : ''}`);
  };

  const exportReorder = () => {
    const data = result || lastResult;
    if (!data || !data.stockRecommendations || data.stockRecommendations.length === 0) return;

    exportToExcel(
      data.stockRecommendations.map((item) => ({
        'Item Name': item.itemName,
        'Item Code': item.itemCode,
        'Unit': item.itemUnit || '',
        'Current Qty': item.quantity,
        'Avg Monthly': item.avgMonthlyConsumption,
        'Current Reorder Level': item.currentThreshold || '',
        'Suggested Level': item.suggestedReorderLevel,
        'Days Until Stockout': item.daysUntilStockout != null ? item.daysUntilStockout : '',
        'Recommendation': item.recommendation,
      })),
      `reorder-suggestions-${store.name}-${getDateStr(data)}.xlsx`
    );
    toast.success('Reorder suggestions exported');
  };

  const _onClose = () => {
    cancelledRef.current = true;
    cleanupProgress();
    setState('idle');
    setResult(null);
    setProgress(0);
    setProgressMessage('');
    onClose();
  };

  const accentColor = isDark ? 'rgba(99,102,241,0.8)' : '#6366f1';
  const greenColor = theme.palette.success?.main || '#4caf50';
  const warnColor = theme.palette.warning?.main || '#ff9800';
  const errorColor = theme.palette.error?.main || '#ef4444';
  const greyColor = isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8';

  const displayResult = result || lastResult;

  const severityColor = (severity) => {
    switch (severity) {
      case 'error': return errorColor;
      case 'warning': return warnColor;
      case 'info': return greyColor;
      default: return greyColor;
    }
  };

  const severityBg = (severity) => {
    switch (severity) {
      case 'error': return isDark ? 'rgba(239,68,68,0.12)' : 'rgba(239,68,68,0.08)';
      case 'warning': return isDark ? 'rgba(255,152,0,0.12)' : 'rgba(255,152,0,0.08)';
      case 'info': return isDark ? 'rgba(255,255,255,0.06)' : 'rgba(148,163,184,0.08)';
      default: return isDark ? 'rgba(255,255,255,0.06)' : 'rgba(148,163,184,0.08)';
    }
  };

  // Summary chips
  const renderChips = () => {
    if (!displayResult) return null;
    const chips = TABS.filter((tab) => getTabCount(displayResult, tab.key) > 0);
    if (chips.length === 0) return null;

    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
        {chips.map((tab) => {
          const count = getTabCount(displayResult, tab.key);
          const isActive = activeTab === tab.key;
          const color = severityColor(tab.severity);
          return (
            <div
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '4px 10px',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
                backgroundColor: isActive ? severityBg(tab.severity) : 'transparent',
                border: `1px solid ${isActive ? color : (isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0')}`,
                color: isActive ? color : (isDark ? 'rgba(255,255,255,0.5)' : '#64748b'),
                transition: 'all 0.15s',
              }}>
              <span style={{ fontWeight: 600 }}>{count}</span> {tab.label}
            </div>
          );
        })}
      </div>
    );
  };

  // Generic table renderer
  const renderTable = (columns, rows) => {
    if (!rows || rows.length === 0) return null;
    return (
      <div style={{
        borderRadius: 8,
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
        overflow: 'hidden',
        marginTop: 12,
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc' }}>
              {columns.map((col) => (
                <th key={col.key} style={{ ...thStyle(isDark), textAlign: col.align || 'left' }}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.storeItemId || i} style={{
                borderTop: i > 0 ? `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}` : undefined,
              }}>
                {columns.map((col) => (
                  <td key={col.key} style={{
                    ...tdStyle(isDark),
                    textAlign: col.align || 'left',
                    fontFamily: col.mono ? 'monospace' : undefined,
                    color: col.colorFn ? col.colorFn(row) : undefined,
                    fontWeight: col.boldFn && col.boldFn(row) ? 600 : undefined,
                  }}>
                    {col.render ? col.render(row) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Item cell with name + code
  const itemCell = (row) => (
    <>
      <div>{row.itemName}</div>
      {row.itemCode && (
        <div style={{ fontSize: 11, color: isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8' }}>
          {row.itemCode}
        </div>
      )}
    </>
  );

  // Badge/pill renderer
  const pill = (text, color, bgOverride) => (
    <span style={{
      fontSize: 11,
      padding: '2px 8px',
      borderRadius: 4,
      backgroundColor: bgOverride || (isDark ? `${color}20` : `${color}14`),
      color,
    }}>
      {text}
    </span>
  );

  // Tab content renderers
  const renderActiveTabContent = () => {
    if (!displayResult) return null;

    switch (activeTab) {
      case 'mismatches':
        return renderTable(
          [
            {
              key: 'item', label: 'Item',
              render: (r) => {
                let explanation = '';
                if (!r.quantityMatch) {
                  explanation = `Should be ${r.derivedQuantity} based on transactions, but showing ${r.storedQuantity}. Use Auto-Correct to fix.`;
                } else if (r.lastTxRateIssue) {
                  const m = r.lastTxRateIssue.match(/\(([^)]+)\).*\(([^)]+)\)/);
                  if (m) {
                    explanation = `Someone used Edit Rate to change it to ${m[1]} — last purchase had set it to ${m[2]}.`;
                  }
                } else if (r.rateSanityIssue) {
                  explanation = `Rate is invalid (${r.rateSanityIssue}). Set a correct rate via Modify.`;
                }
                return (
                  <>
                    {itemCell(r)}
                    {explanation && (
                      <div style={{ fontSize: 11, color: isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8', marginTop: 3, lineHeight: 1.4 }}>
                        {explanation}
                      </div>
                    )}
                  </>
                );
              },
            },
            { key: 'storedQuantity', label: 'Stored', align: 'right', mono: true },
            { key: 'derivedQuantity', label: 'Derived', align: 'right', mono: true },
            {
              key: 'quantityDrift', label: 'Drift', align: 'right', mono: true,
              colorFn: (r) => r.quantityDrift !== 0 ? '#ef4444' : undefined,
              boldFn: (r) => r.quantityDrift !== 0,
              render: (r) => `${r.quantityDrift > 0 ? '+' : ''}${r.quantityDrift}`,
            },
            {
              key: 'issue', label: 'Issue',
              render: (r) => {
                if (!r.quantityMatch) return pill('Qty drift', '#ef4444');
                if (r.rateSanityIssue) return pill(r.rateSanityIssue, '#ef4444');
                if (r.lastTxRateIssue) {
                  const m = r.lastTxRateIssue.match(/\(([^)]+)\).*\(([^)]+)\)/);
                  if (m) return pill(`Rate: ${m[1]} → ${m[2]}`, warnColor);
                  return pill('Rate changed', warnColor);
                }
                return pill('Rate', warnColor);
              },
            },
          ],
          displayResult.mismatches
        );

      case 'negative':
        return renderTable(
          [
            { key: 'item', label: 'Item', render: itemCell },
            { key: 'itemCode', label: 'Code' },
            {
              key: 'quantity', label: 'Quantity', align: 'right', mono: true,
              colorFn: () => '#ef4444',
              boldFn: () => true,
            },
          ],
          displayResult.negativeQuantities
        );

      case 'outOfStock':
        return renderTable(
          [
            { key: 'item', label: 'Item', render: itemCell },
            {
              key: 'avgMonthlyConsumption', label: 'Avg Monthly', align: 'right', mono: true,
              render: (r) => r.avgMonthlyConsumption > 0 ? r.avgMonthlyConsumption : '—',
            },
            {
              key: 'daysSinceLastOut', label: 'Days Inactive', align: 'right', mono: true,
              render: (r) => r.daysSinceLastOut != null ? `${r.daysSinceLastOut}d` : '—',
              colorFn: (r) => r.daysSinceLastOut != null && r.daysSinceLastOut > 60 ? warnColor : undefined,
            },
            {
              key: 'recommendation', label: 'Status',
              render: (r) => {
                if (r.recommendation === 'reorder') return pill('Reorder', warnColor);
                if (r.recommendation === 'archive') return pill('Archive', greyColor);
                return '—';
              },
            },
          ],
          displayResult.outOfStock
        );

      case 'reorder': {
        const activeRecs = (displayResult.stockRecommendations || []).filter(
          (r) => r.recommendation !== 'archive'
        );

        const reorderColumns = [
          {
            key: 'item', label: 'Item',
            render: (r) => {
              let hint = '';
              if (r.recommendation === 'set_reorder') {
                hint = `Uses ~${r.avgMonthlyConsumption}/mo but has no reorder alert`;
              } else if (r.recommendation === 'reorder_now' && r.quantity <= 0) {
                hint = `Out of stock — uses ~${r.avgMonthlyConsumption}/mo`;
              } else if (r.recommendation === 'reorder_now') {
                hint = `Below reorder level (${r.currentThreshold}) — ${r.daysUntilStockout != null ? `~${Math.round(r.daysUntilStockout)}d left` : 'restock needed'}`;
              } else if (r.recommendation === 'review') {
                hint = `${r.daysUntilStockout != null ? `~${Math.round(r.daysUntilStockout)} days` : 'Low stock'} at current usage`;
              }
              return (
                <>
                  {itemCell(r)}
                  {hint && (
                    <div style={{ fontSize: 11, color: isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8', marginTop: 2 }}>
                      {hint}
                    </div>
                  )}
                </>
              );
            },
          },
          { key: 'quantity', label: 'Qty', align: 'right', mono: true },
          {
            key: 'avgMonthlyConsumption', label: 'Avg/Mo', align: 'right', mono: true,
            render: (r) => r.avgMonthlyConsumption > 0 ? r.avgMonthlyConsumption : '—',
          },
          {
            key: 'currentThreshold', label: 'Current', align: 'right', mono: true,
            render: (r) => r.currentThreshold > 0 ? r.currentThreshold : '—',
          },
          {
            key: 'suggestedReorderLevel', label: 'Suggested', align: 'right', mono: true,
            colorFn: () => accentColor,
            boldFn: () => true,
            render: (r) => r.suggestedReorderLevel > 0 ? r.suggestedReorderLevel : '—',
          },
          {
            key: 'recommendation', label: 'Status',
            render: (r) => {
              if (appliedItems.has(r.storeItemId)) return pill(`Set to ${appliedItems.get(r.storeItemId)}`, greenColor);
              switch (r.recommendation) {
                case 'set_reorder': return pill('Set Reorder', accentColor);
                case 'reorder_now': return pill('Reorder Now', errorColor);
                case 'review': return pill('Low Stock', warnColor);
                default: return '—';
              }
            },
          },
          {
            key: 'action', label: 'Set Level',
            render: (r) => {
              if (appliedItems.has(r.storeItemId)) return null;
              const inputId = 'reorder-input-' + r.storeItemId;
              const applyLevel = async (value) => {
                const num = parseInt(value, 10);
                if (!num || num <= 0) {
                  toast.error('Enter a valid reorder level');
                  return;
                }
                try {
                  await storesMng.updateStoreItemThreshold({
                    storeItemId: r.storeItemId,
                    lowStockThreshold: num,
                  });
                  setAppliedItems((prev) => new Map([...prev, [r.storeItemId, num]]));
                  toast.success(`Set reorder level for ${r.itemName} to ${num}`);
                } catch (e) {
                  toast.error(`Failed: ${r.itemName}`);
                }
              };
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input
                    id={inputId}
                    type="number"
                    defaultValue={r.suggestedReorderLevel > 0 ? r.suggestedReorderLevel : ''}
                    placeholder="Level"
                    style={{
                      width: 60,
                      padding: '3px 6px',
                      fontSize: 12,
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#d1d5db'}`,
                      borderRadius: 4,
                      backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#fff',
                      color: isDark ? '#e2e8f0' : '#1e293b',
                      outline: 'none',
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') applyLevel(e.target.value);
                    }}
                  />
                  <Button
                    size="small"
                    onClick={() => {
                      const el = document.getElementById(inputId);
                      applyLevel(el ? el.value : '');
                    }}
                    style={{ fontSize: 11, padding: '2px 8px', minWidth: 0, color: accentColor }}>
                    Set
                  </Button>
                </div>
              );
            },
          },
        ];

        return renderTable(reorderColumns, activeRecs);
      }

      case 'archive': {
        const archiveItems = (displayResult.stockRecommendations || []).filter(
          (r) => r.recommendation === 'archive'
        );
        return renderTable(
          [
            { key: 'item', label: 'Item', render: itemCell },
            { key: 'itemCode', label: 'Code' },
            { key: 'itemUnit', label: 'Unit' },
            {
              key: 'action', label: '',
              render: (r) => {
                if (archivedItems.has(r.storeItemId)) return pill('Archived', greenColor);
                return (
                  <Button
                    size="small"
                    onClick={() => archiveItem(r)}
                    style={{ fontSize: 11, padding: '2px 8px', minWidth: 0, color: greyColor }}>
                    Archive
                  </Button>
                );
              },
            },
          ],
          archiveItems
        );
      }

      case 'stale':
        return renderTable(
          [
            { key: 'item', label: 'Item', render: itemCell },
            { key: 'itemCode', label: 'Code' },
            { key: 'storedQuantity', label: 'Stored Qty', align: 'right', mono: true },
            {
              key: 'action', label: '',
              render: (r) => {
                if (archivedItems.has(r.storeItemId)) return pill('Archived', greenColor);
                return (
                  <Button
                    size="small"
                    onClick={() => archiveItem(r)}
                    style={{ fontSize: 11, padding: '2px 8px', minWidth: 0, color: greyColor }}>
                    Archive
                  </Button>
                );
              },
            },
          ],
          displayResult.staleItems
        );

      case 'modifies':
        return renderTable(
          [
            { key: 'item', label: 'Item', render: itemCell },
            { key: 'itemCode', label: 'Code' },
            {
              key: 'modifyCount', label: 'Modify Count', align: 'right', mono: true,
              colorFn: () => warnColor,
              boldFn: () => true,
            },
          ],
          displayResult.frequentModifies
        );

      case 'rates':
        return renderTable(
          [
            { key: 'item', label: 'Item', render: (r) => itemCell(r) },
            { key: 'storedQuantity', label: 'Qty', align: 'right', mono: true },
            {
              key: 'suggestedRate', label: 'Last Known', align: 'right', mono: true,
              render: (r) => r.suggestedRate ? (
                <span style={{ color: accentColor, fontWeight: 600 }}>{r.suggestedRate}</span>
              ) : '—',
            },
            {
              key: 'action', label: 'Set Rate',
              render: (r) => {
                if (appliedItems.has('rate-' + r.storeItemId)) return pill(`Rate set to ${appliedItems.get('rate-' + r.storeItemId)}`, greenColor);
                const inputId = 'rate-input-' + r.storeItemId;
                const applyRate = async (value) => {
                  const num = parseFloat(value);
                  if (!num || num <= 0 || !Number.isFinite(num)) {
                    toast.error('Enter a valid rate');
                    return;
                  }
                  try {
                    await storesMng.storeItem.actions.updateStoreItemAdjustedRate({
                      storeItemId: r.storeItemId,
                      adjustedRateValue: num,
                    });
                    setAppliedItems((prev) => new Map([...prev, ['rate-' + r.storeItemId, num]]));
                    toast.success(`Set rate for ${r.itemName} to ${num}`);
                  } catch (e) {
                    toast.error(`Failed to set rate for ${r.itemName}`);
                  }
                };
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input
                      id={inputId}
                      type="number"
                      step="any"
                      defaultValue={r.suggestedRate || ''}
                      placeholder="Rate"
                      style={{
                        width: 70,
                        padding: '3px 6px',
                        fontSize: 12,
                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#d1d5db'}`,
                        borderRadius: 4,
                        backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#fff',
                        color: isDark ? '#e2e8f0' : '#1e293b',
                        outline: 'none',
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') applyRate(e.target.value);
                      }}
                    />
                    <Button
                      size="small"
                      onClick={() => {
                        const el = document.getElementById(inputId);
                        applyRate(el ? el.value : '');
                      }}
                      style={{ fontSize: 11, padding: '2px 8px', minWidth: 0, color: accentColor }}>
                      Set
                    </Button>
                  </div>
                );
              },
            },
          ],
          displayResult.warnings
        );

      default:
        return null;
    }
  };

  // Export for current tab
  const exportCurrentTab = () => {
    switch (activeTab) {
      case 'mismatches': return exportMismatches();
      case 'negative': return exportNegatives();
      case 'outOfStock': return exportOutOfStock();
      case 'reorder': return exportReorder();
      case 'archive': return exportReorder();
      case 'stale': return exportStale();
      case 'modifies': return exportModifies();
      case 'rates': return exportMissingRates();
      default: return;
    }
  };

  const renderTabDescription = () => {
    const tab = TABS.find((t) => t.key === activeTab);
    if (!tab || !tab.description) return null;
    const count = getTabCount(displayResult, activeTab);
    return (
      <div style={{
        marginTop: 14,
        padding: '10px 14px',
        borderRadius: 8,
        backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0'}`,
      }}>
        <div style={{
          fontSize: 13,
          fontWeight: 600,
          color: severityColor(tab.severity),
          marginBottom: 4,
        }}>
          {count} {tab.label}
        </div>
        <div style={{
          fontSize: 12,
          lineHeight: 1.5,
          color: isDark ? 'rgba(255,255,255,0.45)' : '#64748b',
          whiteSpace: 'pre-line',
        }}>
          {tab.description}
        </div>
      </div>
    );
  };

  // Whether current tab has an action button
  const renderTabActions = () => {
    const hasData = displayResult && getTabCount(displayResult, activeTab) > 0;
    if (!hasData) return null;

    return (
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        {activeTab === 'mismatches' && result && result.mismatchedItems > 0 && (
          <Button
            size="small"
            onClick={correctAll}
            disabled={correcting}
            style={{ color: warnColor }}
            startIcon={<IFix size={13} />}>
            {correcting ? 'Correcting...' : 'Auto-Correct All'}
          </Button>
        )}
        {activeTab === 'reorder' && displayResult && displayResult.stockRecommendations && (() => {
          const applicable = displayResult.stockRecommendations.filter(
            (r) => (r.recommendation === 'set_reorder' || r.recommendation === 'reorder_now' || r.recommendation === 'review')
              && r.suggestedReorderLevel > 0
              && !appliedItems.has(r.storeItemId)
          );
          if (applicable.length === 0) return null;
          return (
            <Button
              size="small"
              onClick={applyAllReorderSuggestions}
              disabled={applyingReorder}
              style={{ color: accentColor }}
              startIcon={<IFix size={13} />}>
              {applyingReorder
                ? `Applying ${applyProgress.done}/${applyProgress.total}...`
                : `Apply All Suggestions (${applicable.length} items)`}
            </Button>
          );
        })()}
        {activeTab === 'archive' && displayResult && displayResult.stockRecommendations && (() => {
          const archiveItems = displayResult.stockRecommendations.filter(
            (r) => r.recommendation === 'archive' && !archivedItems.has(r.storeItemId)
          );
          if (archiveItems.length === 0) return null;
          return (
            <Button
              size="small"
              onClick={() => archiveAllItems(archiveItems)}
              disabled={archiving}
              style={{ color: greyColor }}
              startIcon={<IFix size={13} />}>
              {archiving
                ? `Archiving ${archiveProgress.done}/${archiveProgress.total}...`
                : `Archive All (${archiveItems.length} items)`}
            </Button>
          );
        })()}
        {activeTab === 'stale' && displayResult && displayResult.staleItems && (() => {
          const staleToArchive = displayResult.staleItems.filter(
            (r) => !archivedItems.has(r.storeItemId)
          );
          if (staleToArchive.length === 0) return null;
          return (
            <Button
              size="small"
              onClick={() => archiveAllItems(staleToArchive)}
              disabled={archiving}
              style={{ color: greyColor }}
              startIcon={<IFix size={13} />}>
              {archiving
                ? `Archiving ${archiveProgress.done}/${archiveProgress.total}...`
                : `Archive All (${staleToArchive.length} items)`}
            </Button>
          );
        })()}
        {activeTab === 'negative' && result && result.negativeQuantities && result.negativeQuantities.length > 0 && (
          <Button
            size="small"
            onClick={correctNegatives}
            disabled={correcting}
            style={{ color: errorColor }}
            startIcon={<IFix size={13} />}>
            {correcting ? 'Correcting...' : 'Correct All to Zero'}
          </Button>
        )}
        <Button
          size="small"
          onClick={exportCurrentTab}
          color="primary"
          startIcon={<IExport size={13} />}>
          Generate Report
        </Button>
      </div>
    );
  };

  return (
    <Dialog
      className={'fullScreenMobile'}
      open={open}
      onClose={state !== 'running' ? _onClose : undefined}
      maxWidth="sm"
      fullWidth>
      <DialogTitle style={{ paddingBottom: 0 }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}>
          <div style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            backgroundColor: isDark ? 'rgba(99,102,241,0.12)' : '#eef2ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: accentColor,
            flexShrink: 0,
          }}>
            <IHealth size={18} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: 16,
              fontWeight: 600,
              color: isDark ? '#e2e8f0' : '#1e293b',
            }}>
              Inventory Verification
            </div>
            <div style={{
              fontSize: 12,
              color: isDark ? 'rgba(255,255,255,0.45)' : '#94a3b8',
              marginTop: 1,
            }}>
              {store?.name}
            </div>
          </div>
        </div>
        <CloseBtn onClick={_onClose} />
      </DialogTitle>
      <DialogContent>
        <Box style={{ padding: '16px 0' }}>
          {/* Loading state — removed, last result loads in background */}

          {/* Progress bar while running */}
          {state === 'running' && (
            <Box style={{ textAlign: 'center', padding: '24px 0' }}>
              <Typography variant="body1" style={{ marginBottom: 16 }}>
                {progressMessage}
              </Typography>
              <LinearProgress
                variant={progress >= 100 ? 'indeterminate' : 'determinate'}
                value={progress}
                style={{ height: 6, borderRadius: 3 }}
              />
              {progress < 100 && (
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                  {Math.round(progress)}%
                </Typography>
              )}
              {stale ? (
                <Typography variant="body2" style={{ marginTop: 12, color: isDark ? '#fbbf24' : '#d97706' }}>
                  No updates received for a while. The report may still be running on the server — please keep this tab open.
                </Typography>
              ) : (
                <Typography variant="caption" color="textSecondary" style={{ marginTop: 12, display: 'block' }}>
                  Please keep this tab open while the report generates.
                </Typography>
              )}
            </Box>
          )}

          {/* Error state */}
          {state === 'error' && (
            <Box style={{ textAlign: 'center', padding: '24px 0' }}>
              <IError style={{ fontSize: 48, color: theme.palette.error?.main || '#f44336' }} />
              <Typography variant="subtitle1" style={{ fontWeight: 600, marginTop: 10 }}>
                Verification Failed
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
                {progressMessage}
              </Typography>
            </Box>
          )}

          {/* Done/idle — summary + tabs */}
          {(state === 'done' || state === 'idle') && displayResult && (
            <>
              {/* Stale report banner — show prominently when viewing an older result */}
              {state === 'idle' && displayResult.verifiedAt && (() => {
                const ageMs = Date.now() - new Date(displayResult.verifiedAt).getTime();
                const ageHours = ageMs / (1000 * 60 * 60);
                const ageDays = Math.floor(ageHours / 24);
                const isStale = ageHours > 24;
                const ageLabel = ageDays > 0
                  ? `${ageDays} day${ageDays > 1 ? 's' : ''} ago`
                  : `${Math.floor(ageHours)} hour${Math.floor(ageHours) !== 1 ? 's' : ''} ago`;

                return (
                  <Box style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 14px',
                    borderRadius: 8,
                    marginBottom: 12,
                    backgroundColor: isStale
                      ? (isDark ? 'rgba(255,152,0,0.08)' : '#fffbeb')
                      : (isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc'),
                    border: `1px solid ${isStale
                      ? (isDark ? 'rgba(255,152,0,0.2)' : '#fde68a')
                      : (isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0')}`,
                  }}>
                    {isStale && <IWarn style={{ color: warnColor, fontSize: 16, flexShrink: 0 }} />}
                    <div>
                      <div style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: isStale ? warnColor : (isDark ? '#e2e8f0' : '#1e293b'),
                      }}>
                        Last verified {ageLabel}
                      </div>
                      <div style={{
                        fontSize: 11,
                        color: isDark ? 'rgba(255,255,255,0.4)' : '#94a3b8',
                        marginTop: 1,
                      }}>
                        {new Date(displayResult.verifiedAt).toLocaleDateString()}{' '}
                        {new Date(displayResult.verifiedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        {isStale && ' — Run again for current data'}
                      </div>
                    </div>
                  </Box>
                );
              })()}

              {/* Store summary — inventory value + transaction count */}
              {displayResult.storeSummary && (() => {
                const s = displayResult.storeSummary;
                const txMatch = s.transactionCountMatch;
                const pctWithRate = s.totalActiveItems > 0
                  ? Math.round((s.itemsWithRate / s.totalActiveItems) * 100)
                  : 0;
                return (
                  <Box style={{
                    padding: '12px 14px',
                    borderRadius: 8,
                    marginBottom: 12,
                    backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0'}`,
                  }}>
                    <div style={{
                      fontSize: 11,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      color: isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8',
                      marginBottom: 10,
                    }}>
                      Store Summary
                    </div>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      {/* Inventory Value */}
                      <div style={{ flex: '1 1 140px', minWidth: 0 }}>
                        <div style={{
                          fontSize: 11,
                          color: isDark ? 'rgba(255,255,255,0.4)' : '#94a3b8',
                          marginBottom: 4,
                        }}>
                          Inventory Value
                        </div>
                        <div style={{
                          fontSize: 20,
                          fontWeight: 700,
                          color: isDark ? '#e2e8f0' : '#1e293b',
                        }}>
                          {s.inventoryValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div style={{
                          fontSize: 11,
                          color: s.itemsWithoutRate > 0 ? warnColor : (isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8'),
                          marginTop: 2,
                        }}>
                          {s.itemsWithoutRate > 0
                            ? `Only reflects ${pctWithRate}% of items — ${s.itemsWithoutRate} items have no rate`
                            : `All ${s.totalActiveItems} items have rates`
                          }
                        </div>
                        {s.itemsWithoutRate > 0 && (
                          <div style={{
                            fontSize: 11,
                            color: isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8',
                            marginTop: 3,
                          }}>
                            Add rates via In transaction or Modify to get accurate valuation
                          </div>
                        )}
                      </div>
                      {/* Transaction Count */}
                      <div style={{ flex: '1 1 140px', minWidth: 0 }}>
                        <div style={{
                          fontSize: 11,
                          color: isDark ? 'rgba(255,255,255,0.4)' : '#94a3b8',
                          marginBottom: 4,
                        }}>
                          Transaction Count
                        </div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                          <span style={{
                            fontSize: 20,
                            fontWeight: 700,
                            color: isDark ? '#e2e8f0' : '#1e293b',
                          }}>
                            {s.actualTransactionCount.toLocaleString()}
                          </span>
                          {!txMatch && (
                            <span style={{
                              fontSize: 11,
                              padding: '2px 6px',
                              borderRadius: 4,
                              backgroundColor: isDark ? 'rgba(239,68,68,0.12)' : 'rgba(239,68,68,0.08)',
                              color: errorColor,
                              fontWeight: 500,
                            }}>
                              drift: {s.transactionCountDrift > 0 ? '+' : ''}{s.transactionCountDrift}
                            </span>
                          )}
                        </div>
                        <div style={{
                          fontSize: 11,
                          color: txMatch ? greenColor : errorColor,
                          marginTop: 2,
                        }}>
                          {txMatch
                            ? 'Verified — matches database records'
                            : `Mismatch — store displays ${s.storedTransactionCount.toLocaleString()}`
                          }
                        </div>
                      </div>
                    </div>
                  </Box>
                );
              })()}

              {/* Recommendations */}
              {(() => {
                const recs = [];
                const mismatches = displayResult.mismatchedItems || 0;
                const negatives = displayResult.negativeQuantityCount || 0;
                const missingRates = displayResult.missingRateCount || (displayResult.warnings ? displayResult.warnings.length : 0);
                const stale = displayResult.staleItemCount || (displayResult.staleItems ? displayResult.staleItems.length : 0);
                const outOfStock = displayResult.outOfStockCount || (displayResult.outOfStock ? displayResult.outOfStock.length : 0);
                const modifies = displayResult.frequentModifyCount || (displayResult.frequentModifies ? displayResult.frequentModifies.length : 0);

                const reorderSuggestions = displayResult.reorderSuggestionsCount || 0;
                const lowStockItems = displayResult.lowStockCount || 0;
                const archiveSuggestions = displayResult.archiveSuggestionsCount || 0;

                if (mismatches > 0)
                  recs.push({ severity: 'error', count: mismatches, title: 'Quantity drift', detail: 'Run Auto-Correct or export to review', tab: 'mismatches' });
                if (negatives > 0)
                  recs.push({ severity: 'error', count: negatives, title: 'Negative stock', detail: 'Correct to zero to fix balances', tab: 'negative' });
                if (reorderSuggestions > 0)
                  recs.push({ severity: 'warning', count: reorderSuggestions, title: 'No reorder level', detail: 'Active consumption but no alert threshold — apply suggestions', tab: 'reorder' });
                if (lowStockItems > 0)
                  recs.push({ severity: 'warning', count: lowStockItems, title: 'Below reorder level', detail: 'Restock needed', tab: 'reorder' });
                if (archiveSuggestions > 0)
                  recs.push({ severity: 'info', count: archiveSuggestions, title: 'No consumption in 90+ days', detail: 'Consider archiving to reduce clutter', tab: 'archive' });
                if (missingRates > 0)
                  recs.push({ severity: 'warning', count: missingRates, title: 'No unit rate', detail: 'Inventory value is incomplete — add rate via In or Edit Rate', tab: 'rates' });
                if (stale > 0)
                  recs.push({ severity: 'info', count: stale, title: 'No transaction history', detail: 'Never used — consider removing', tab: 'stale' });
                if (outOfStock > 0)
                  recs.push({ severity: 'warning', count: outOfStock, title: 'Out of stock', detail: 'Restock or archive', tab: 'outOfStock' });
                if (modifies > 0)
                  recs.push({ severity: 'warning', count: modifies, title: 'Frequently modified', detail: 'May indicate process gaps — review workflow', tab: 'modifies' });

                if (recs.length === 0) return null;

                return (
                  <Box style={{
                    padding: '14px 16px',
                    borderRadius: 8,
                    marginBottom: 12,
                    backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0'}`,
                  }}>
                    <div style={{
                      fontSize: 11,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      color: isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8',
                      marginBottom: 12,
                    }}>
                      Recommendations
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {recs.map((rec, i) => (
                        <Tooltip key={i} title={rec.detail} arrow placement="top">
                          <div
                            onClick={() => setActiveTab(rec.tab)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '6px 10px',
                              borderRadius: 6,
                              backgroundColor: severityBg(rec.severity),
                              flex: '0 0 auto',
                              maxWidth: '100%',
                              cursor: 'pointer',
                            }}>
                            <span style={{
                              fontSize: 15,
                              fontWeight: 700,
                              color: severityColor(rec.severity),
                            }}>
                              {rec.count}
                            </span>
                            <span style={{
                              fontSize: 12,
                              color: isDark ? 'rgba(255,255,255,0.7)' : '#334155',
                            }}>
                              {rec.title}
                            </span>
                          </div>
                        </Tooltip>
                      ))}
                    </div>
                  </Box>
                );
              })()}

              {/* Summary header */}
              <Box style={{
                padding: '14px 18px',
                borderRadius: 10,
                backgroundColor: isDark ? 'rgba(99,102,241,0.06)' : 'rgba(99,102,241,0.04)',
                border: `1px solid ${isDark ? 'rgba(99,102,241,0.12)' : 'rgba(99,102,241,0.1)'}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {(displayResult.mismatchedItems || 0) === 0 && (displayResult.negativeQuantityCount || 0) === 0
                    ? <ICheck style={{ color: greenColor, fontSize: 18 }} />
                    : <IWarn style={{ color: warnColor, fontSize: 18 }} />}
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {displayResult.totalItems} items verified
                  </Typography>
                  <span style={{
                    fontSize: 11,
                    marginLeft: 'auto',
                    padding: '2px 8px',
                    borderRadius: 4,
                    backgroundColor: displayResult.includeInactive
                      ? (isDark ? 'rgba(255,152,0,0.12)' : 'rgba(255,152,0,0.08)')
                      : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(148,163,184,0.08)'),
                    color: displayResult.includeInactive ? warnColor : (isDark ? 'rgba(255,255,255,0.4)' : '#94a3b8'),
                  }}>
                    {displayResult.includeInactive ? 'incl. inactive' : 'active only'}
                  </span>
                </div>
                {renderChips()}
              </Box>

              {/* Active tab description + actions + content */}
              {renderTabDescription()}
              {renderTabActions()}
              {renderActiveTabContent()}
            </>
          )}

          {/* Idle with no last result — show scope info + options */}
          {state === 'idle' && !displayResult && (
            <Box>
              <Box style={{
                padding: '14px 16px',
                borderRadius: 8,
                backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0'}`,
                marginBottom: 12,
              }}>
                <Typography variant="body2" style={{ color: isDark ? 'rgba(255,255,255,0.5)' : '#64748b', fontSize: 12, lineHeight: 1.6 }}>
                  The health check scans every item in this store and compares the stored quantity against the quantity derived from transaction history. It also checks for negative quantities, items with no transactions, frequent manual corrections, and missing unit rates.
                </Typography>
              </Box>

              {/* Scope */}
              <Box style={{
                padding: '12px 14px',
                borderRadius: 8,
                backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0'}`,
              }}>
                <div style={{
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8',
                  marginBottom: 10,
                }}>
                  Scope
                </div>
                <div style={{
                  fontSize: 12,
                  color: isDark ? 'rgba(255,255,255,0.5)' : '#64748b',
                  marginBottom: 8,
                }}>
                  Archived items are always excluded from verification.
                </div>
                <div
                  onClick={() => setIncludeInactive(!includeInactive)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    cursor: 'pointer',
                    padding: '4px 0',
                  }}>
                  <Checkbox
                    size="small"
                    checked={includeInactive}
                    onChange={(e) => setIncludeInactive(e.target.checked)}
                    color="primary"
                    style={{ padding: 4 }}
                  />
                  <div>
                    <div style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: isDark ? '#e2e8f0' : '#1e293b',
                    }}>
                      Include inactive items
                    </div>
                    <div style={{
                      fontSize: 11,
                      color: includeInactive ? warnColor : (isDark ? 'rgba(255,255,255,0.4)' : '#94a3b8'),
                      marginTop: 1,
                    }}>
                      {includeInactive
                        ? 'May inflate issue counts and inventory value'
                        : 'Only active items will be verified'
                      }
                    </div>
                  </div>
                </div>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions style={{ padding: '8px 24px 16px' }}>
        {state === 'running' ? (
          <Button onClick={_onClose} color="secondary">
            Cancel
          </Button>
        ) : (
          <>
            <div
              onClick={() => setIncludeInactive(!includeInactive)}
              style={{
                display: 'flex',
                alignItems: 'center',
                marginRight: 'auto',
                cursor: 'pointer',
              }}>
              <Checkbox
                size="small"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                color="primary"
                style={{ padding: 4 }}
              />
              <span style={{
                fontSize: 12,
                color: includeInactive ? (isDark ? '#e2e8f0' : '#1e293b') : (isDark ? 'rgba(255,255,255,0.4)' : '#94a3b8'),
              }}>
                Include inactive items
              </span>
            </div>
            <Button onClick={_onClose} color="primary">
              Close
            </Button>
            <Button
              onClick={runVerification}
              color="primary"
              variant="contained"
              disabled={state === 'loading'}>
              {state === 'done' ? 'Re-Run' : 'Run Verification'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

const thStyle = (isDark) => ({
  padding: '6px 8px',
  textAlign: 'left',
  fontSize: 10,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: isDark ? 'rgba(255,255,255,0.45)' : '#64748b',
  whiteSpace: 'nowrap',
});

const tdStyle = (isDark) => ({
  padding: '8px 8px',
  color: isDark ? '#e2e8f0' : '#1e293b',
});

HealthCheckDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  store: PropTypes.object,
};

export default HealthCheckDialog;
